import { OnboardingType } from "@redotech/cockroachdb-codegen/congodb/onboarding-type";
import {
  createStringEnumSchema,
  selfServeOnboardingStepsConfigurationFieldSchemas,
} from "@redotech/redo-onboarding-common/self-serve-onboarding-configuration";
import * as z from "zod";

export const coverageConfigSchema = z.object({
  titleText: z.string().nullable().optional(),
  subtitleText: z.string().nullable().optional(),
  titleFontSize: z.number().nullable().optional(),
  titleColor: z.string().nullable().optional(),
  subtitleFontSize: z.number().nullable().optional(),
  subtitleColor: z.string().nullable().optional(),
  iconType: z.string().nullable().optional(),
  customLogoUrl: z.string().nullable().optional(),
  backgroundColor: z.string().nullable().optional(),
  cardBorderRadius: z.number().nullable().optional(),
  buttonColor: z.string().nullable().optional(),
  buttonTextColor: z.string().nullable().optional(),
  buttonFontSize: z.number().nullable().optional(),
  buttonBorderRadius: z.number().nullable().optional(),
  optOutText: z.string().nullable().optional(),
  optOutTextColor: z.string().nullable().optional(),
  optOutFontSize: z.number().nullable().optional(),
});

export const selfServeOnboardingStepsConfigurationSchema = z.object({
  steps: z.record(z.string(), z.array(z.string())),
  autoPush: z.boolean().nullish(),
  ...selfServeOnboardingStepsConfigurationFieldSchemas,
  onboardingType: createStringEnumSchema(OnboardingType).nullish(),
});

// Lightweight schema for table rows (minimal fields, no images/configs)
export const selfServeOnboardingRowSchema = z.object({
  id: z.string(),
  merchantId: z.string(),
  name: z.string(),
  industry: z.string(),
  type: z.enum(["Self-Serve", "Custom"]),
  startDateTime: z.string(),
  completionDateTime: z.string().nullable(),
  lastSessionAt: z.string(),
  dealSize: z.number().nullable(),
  onboardingProgress: z.enum([
    "Completed",
    "Reopened",
    "Awaiting approval",
    "In progress",
    "Partial Completion",
    "Help Requested",
  ]),
  pushConfigurationDate: z.string().nullable(),
  // Duration from tracked time stats (in seconds)
  durationSeconds: z.number().nullable(),
  // Total number of sessions for this merchant
  totalSessions: z.number().nullable(),
  // Completion percentage (completed substeps / total substeps)
  completionPercent: z.number().nullable(),
  // Weighted revenue (progress-weighted deal size)
  weightedRevenue: z.number().nullable(),
  // Weight percentage (0-100) based on visited substeps
  weightPercent: z.number().nullable(),
  // Step times data for weight breakdown display
  cumulativeStepTimes: z.any().optional(),
  // Order-editing specific fields (only populated for order-editing product)
  latestOnboardingData: z.any().optional(),
  // Product type (returns or order-editing)
  product: z.enum(["returns", "order-editing"]).optional(),
  // HubSpot deal ID for linking to HubSpot
  hubspotDealId: z.string().nullable().optional(),
  contacted: z.boolean().optional(),
  contactedAt: z.string().nullable().optional(),
  contactedBy: z.string().nullable().optional(),
});

export type SelfServeOnboardingRow = z.infer<
  typeof selfServeOnboardingRowSchema
>;
